import { journeyStorage } from '@epilot/journey-logic-commons'

import type { SsoFlowRecord } from './types'

const FLOW_KEY_PREFIX = 'journey.sso.flow.'

/** Abandoned flows older than this can never complete (the IdP code has long
 * expired), so they are safe to purge together with their snapshots. */
const FLOW_MAX_AGE_MS = 60 * 60 * 1000

const flowKey = (state: string) => `${FLOW_KEY_PREFIX}${state}`

const parseRecord = (raw: string | null): SsoFlowRecord | null => {
  if (!raw) return null

  try {
    return JSON.parse(raw) as SsoFlowRecord
  } catch {
    return null
  }
}

/** Persists the flow record and read-verifies it: the record is the only bridge across the
 * IdP round-trip, so a silent write failure must surface as `null` before any redirect. */
export const createFlow = (
  record: Omit<SsoFlowRecord, 'created_at' | 'status'>
): SsoFlowRecord | null => {
  const flow: SsoFlowRecord = {
    ...record,
    created_at: Date.now(),
    status: 'started'
  }

  const written = journeyStorage.setItem(
    flowKey(flow.state),
    JSON.stringify(flow)
  )

  return written && getFlow(flow.state) ? flow : null
}

export const getFlow = (state: string): SsoFlowRecord | null =>
  parseRecord(journeyStorage.getItem(flowKey(state)))

/** Synchronously marks the flow as consumed, so a duplicated tab or a remount
 * sees `consumed` and never posts the single-use authorization code twice. */
export const consumeFlow = (state: string): SsoFlowRecord | null => {
  const flow = getFlow(state)

  if (!flow || flow.status === 'consumed') return null

  const consumed: SsoFlowRecord = { ...flow, status: 'consumed' }

  journeyStorage.setItem(flowKey(state), JSON.stringify(consumed))

  return consumed
}

export const deleteFlow = (state: string): void => {
  journeyStorage.removeItem(flowKey(state))
}

export const purgeStaleFlows = (): void => {
  try {
    const now = Date.now()
    const staleKeys: string[] = []

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index)

      if (!key?.startsWith(FLOW_KEY_PREFIX)) continue

      const flow = parseRecord(localStorage.getItem(key))

      if (!flow?.created_at || now - flow.created_at > FLOW_MAX_AGE_MS) {
        staleKeys.push(key)
      }
    }

    staleKeys.forEach((key) => localStorage.removeItem(key))
  } catch {
    // storage can be unavailable (blocked 3rd-party storage); purging is best-effort
  }
}
